import { ZfDialog, ZfDialogContent, ZfDialogFooter, ZfLoadingSpinner, ZfStack, ZfTextButton } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useClientPackages } from '../api';
import { ClientPackageEditor } from '../components/client-package/ClientPackageEditor';
import { DocumentTableContainer } from '../components/client-package/DocumentTableContainer';
import { CrossBorderWarning } from '../components/cross-border';

export const DocumentsScreen: FunctionComponent = () => {
    const { t } = useTranslation();
    const { packages, isLoading } = useClientPackages();
    const { packageId } = useParams();

    const [finaliseConfirmationPopupOpen, setFinaliseConfirmationPopupOpen] = useState<boolean>(false);
    const [finalisationCallbackFn, setFinalisationCallbackFn] = useState<(() => void) | undefined>(undefined);

    const handleFinalisePopupClose = () => {
        setFinaliseConfirmationPopupOpen(false);
        setFinalisationCallbackFn(undefined);
    };

    const handleFinaliseClick = (callbackFn: () => void) => {
        setFinaliseConfirmationPopupOpen(true);
        setFinalisationCallbackFn(() => callbackFn);
    };

    const handleFinaliseConfirm = () => {
        setFinaliseConfirmationPopupOpen(false);
        finalisationCallbackFn?.();
    };

    const displayedClientPackage = useMemo(
        () => (packages.length && !!packageId ? packages.find((clientPackage) => clientPackage.id === parseInt(packageId)) : undefined),
        [packages, packageId],
    );

    return (
        <>
            {isLoading ? (
                <ZfLoadingSpinner style={{ position: 'absolute', top: '50%', left: '50%' }} />
            ) : (
                <div className="document-screen">
                    <DocumentTableContainer />
                    <ZfDialog show={finaliseConfirmationPopupOpen} size="s" showCloseButton={false} onDialogClose={handleFinalisePopupClose}>
                        <ZfDialogContent>
                            <ZfStack direction="column" spacing="medium">
                                <CrossBorderWarning variant="banner" />
                                {t('clientPackageFinaliseConfirmationText')}
                            </ZfStack>
                        </ZfDialogContent>
                        <ZfDialogFooter>
                            <ZfStack slot="primary" direction="row" spacing="small">
                                <ZfTextButton size="medium" hierarchy="tertiary" onClick={() => handleFinalisePopupClose()}>
                                    {t('clientPackageGoBack')}
                                </ZfTextButton>
                                <ZfTextButton size="medium" onClick={() => handleFinaliseConfirm()}>
                                    {t('clientPackageActionFinalise')}
                                </ZfTextButton>
                            </ZfStack>
                        </ZfDialogFooter>
                    </ZfDialog>
                    {!!packageId && !!displayedClientPackage && (
                        <ClientPackageEditor displayedClientPackage={displayedClientPackage} onConfirmFinalize={handleFinaliseClick} />
                    )}
                </div>
            )}
        </>
    );
};
